import * as React from 'react';
import { useIntl } from '@leagueplatform/locales';
import {
  EntityDetailsHeader,
  EntityActionMenuItem,
  EntityDataTableModal,
} from '@web-config-app/core-react-ui';
import {
  useEntityDetailsProps,
  useEntityWorkflowActions,
  useEntityHeaderSaveStatus,
} from '@web-config-app/entity-form';
import type {
  WorkflowActionMenuItem,
  CustomWorkflowAction,
  EnvironmentKey,
  DomainEntity,
  ConfigEntityDetail,
} from '@web-config-app/core';
import {
  getEntityStatusBadgeProps,
  EntityDetailActionType,
  EntityStatusValue,
  EntityConfigCustomOperation,
  EntityConfigMultiStepCustomOperation,
  hasEntityOperation,
} from '@web-config-app/core';
import { isUsingConfigAppBackend } from '@web-config-app/common';
import {
  useEntityDetailsEnvironmentSelect,
  useDeepPublish,
  useConfigUserFeedback,
} from '@web-config-app/core-react';
import type { GetEntityDetailsPathForEnvAndId } from '@web-config-app/core-react';
import { CustomEntityActionMenuItem } from '../custom-entity-action-menu-item/custom-entity-action-menu-item.component';
import { CustomEntityMultiStepActionMenuItem } from '../custom-entity-multi-step-action-menu-item/custom-entity-multi-step-action-menu-item.component';
import { DeepCloneModalContainer } from '../deep-clone-modal-container/deep-clone-modal.container';
import { DeepPublishModalContainer } from '../deep-publish-modal-container/deep-publish-modal.container';
import { BulkActionReportingModalContainer } from '../bulk-action-reporting-modal/bulk-action-reporting-modal.container';
import { useGetEntityDetailPath } from '../../hooks/use-get-entity-detail-path/use-get-entity-detail-path';

interface EntityDetailsHeaderContainerProps {
  isLoading: boolean;
  domainEntity: DomainEntity;
  entityListPath: string;
  environmentKey: EnvironmentKey;
  environmentName: string;
  getEntityDetailsPathForEnvAndId: GetEntityDetailsPathForEnvAndId;
  setEntityName: (name: string) => void;
  publishDangerously: boolean;
}

const isCustomAction = (
  item?: WorkflowActionMenuItem | CustomWorkflowAction,
): item is CustomWorkflowAction =>
  typeof item !== 'undefined' && item.type === 'custom';

const isMultiStepOperation = (
  operation: EntityConfigCustomOperation | EntityConfigMultiStepCustomOperation,
): operation is EntityConfigMultiStepCustomOperation => 'steps' in operation;

export const EntityDetailsHeaderContainer = ({
  isLoading = false,
  domainEntity,
  entityListPath,
  environmentKey,
  getEntityDetailsPathForEnvAndId,
  setEntityName,
  publishDangerously,
  environmentName,
}: EntityDetailsHeaderContainerProps) => {
  const { formatMessage } = useIntl();

  const { labelKey, schema } = domainEntity;
  const entityConfigName = formatMessage({ id: labelKey }, { count: 1 });

  const {
    operations,
    name,
    status,
    instanceId,
    entityRootData,
    otherAuthoringEnvironmentState,
    isReadOnly,
    resetEntityData,
    includedEntities,
    defaultEntityName,
    nodeMap: entityTreeNodeMap,
  } = useEntityDetailsProps();

  const shouldUseDomainEntityId = isUsingConfigAppBackend({
    entityLevelOverride: domainEntity.isUsingConfigAppBackend,
  });
  const entityInstanceId = shouldUseDomainEntityId
    ? entityRootData?.attributes?.entityMetadata?.domainEntityId
    : instanceId;

  const { isEntitySaved, message: saveStatusMessage } =
    useEntityHeaderSaveStatus({
      operations,
      entityRootData,
      status,
    });

  const {
    currentAuthoringEnvironment,
    availableEnvironments,
    multiEnvironmentAuthoringEnabled,
    multiEnvironmentActions,
    isEntitySingleEnv,
    isSyncModalOpen,
    setIsSyncModalOpen,
    getEnvironmentDisplayName,
  } = useEntityDetailsEnvironmentSelect({
    status,
    entityName: entityConfigName,
    getEntityDetailsPathForEnvAndId,
    environmentKey,
    entityInstanceId: instanceId,
    otherAuthoringEnvironmentState,
    isEntitySaved,
    domainEntity,
    isReadOnly,
    entityData: entityRootData,
  });

  const {
    primaryAction,
    secondaryAction,
    menuItems: workflowMenuItems = [],
    isDeepCloneModalOpen,
    setIsDeepCloneModalOpen,
    isDeepPublishModalOpen,
    setIsDeepPublishModalOpen,
  } = useEntityWorkflowActions({
    operations,
    domainEntity,
    status,
    isEntitySaved,
    entityConfigName,
    entityListPath,
    environmentName,
    publishDangerously,
    isReadOnly,
    resetEntityData,
    includedEntities,
  });

  const {
    bulkActionReportingStore,
    bulkActionReportingSession,
    clearBulkActionReportingSession,
  } = operations;

  const getEntityDetailPath = useGetEntityDetailPath();
  const getEntityDetailPathForEnvironment = React.useCallback(
    (entityType: string, entityId: string) =>
      getEntityDetailPath(entityType, entityId, currentAuthoringEnvironment),
    [currentAuthoringEnvironment, getEntityDetailPath],
  );

  const deepPublish = useDeepPublish({
    entity: domainEntity,
    environment: currentAuthoringEnvironment,
    schema: domainEntity.schema,
    domainEntityType: domainEntity.type,
    bulkActionReportingStore,
    getEntityDetailPath: getEntityDetailPathForEnvironment,
  });

  // Stable ref for discoverTree so the effect below only fires when
  // isDeepPublishModalOpen or entityRootData change, not when the
  // callback identity shifts due to upstream dependency changes.
  const discoverTreeRef = React.useRef(deepPublish.discoverTree);
  // Intentionally has no dependency array — this keeps the ref pointing at
  // the latest discoverTree callback on every render without triggering the
  // discovery effect below.
  React.useEffect(() => {
    discoverTreeRef.current = deepPublish.discoverTree;
  });

  React.useEffect(() => {
    if (isDeepPublishModalOpen && entityRootData) {
      // entityRootData is EntityRootData (Partial<EntityDetail>); discoverTree
      // expects Partial<ConfigEntityDetail>. A full type guard is impractical
      // here since both are broad partial types from the same data source.
      discoverTreeRef.current(entityRootData as Partial<ConfigEntityDetail>);
    }
  }, [isDeepPublishModalOpen, entityRootData]);

  const handleDeepPublishConfirm = React.useCallback(() => {
    setIsDeepPublishModalOpen(false);
    deepPublish.executeDeepPublish();
  }, [setIsDeepPublishModalOpen, deepPublish]);

  const { setErrorFilterKeywordsActionOverride } = useConfigUserFeedback(
    (state) => ({
      setErrorFilterKeywordsActionOverride:
        state.setErrorFilterKeywordsActionOverride,
    }),
  );

  const handleDeepPublishClose = React.useCallback(() => {
    setIsDeepPublishModalOpen(false);
    deepPublish.reset();
    // Reset the validation error filter override that was set when the
    // publish button's validateBeforePublish() opened the modal. Without
    // this, the override persists for the rest of the session and affects
    // which validation errors are shown after the modal is closed.
    setErrorFilterKeywordsActionOverride(null);
  }, [
    setIsDeepPublishModalOpen,
    deepPublish,
    setErrorFilterKeywordsActionOverride,
  ]);

  const handleReportingModalOpenChange = React.useCallback(
    (open: boolean) => {
      if (!open) {
        // Cancel any in-flight deep publish loop before clearing the session.
        // Without this, the loop continues in the background and calls
        // store.completeSession() against an already-cleared store.
        deepPublish.cancelPublish();
        clearBulkActionReportingSession();
        deepPublish.reset();
      }
    },
    [clearBulkActionReportingSession, deepPublish],
  );

  const handleRetryPublish = React.useCallback(() => {
    deepPublish.executeDeepPublish();
  }, [deepPublish]);

  const handleCancelPublish = React.useCallback(() => {
    deepPublish.cancelPublish();
    clearBulkActionReportingSession();
    deepPublish.reset();
  }, [clearBulkActionReportingSession, deepPublish]);

  const handleReportingModalDone = React.useCallback(() => {
    clearBulkActionReportingSession();
    deepPublish.reset();
  }, [clearBulkActionReportingSession, deepPublish]);

  /**
   * Since we can't handle custom actions the same way as predefined actions
   * (which are passed, via `useEntityOperations`, as `operations`) and incorporated
   * into `workflowMenuItems`, we need come up with a way to associate the clicked
   * menu item with its corresponding action.
   *
   * This should be rectified in https://everlong.atlassian.net/browse/CACT-1844
   */
  const [customActionsMap, setCustomActionsMap] = React.useState<
    Record<string, () => void>
  >({});

  const registerCustomMenuAction = React.useCallback(
    (itemId: string, action: () => void) => {
      if (customActionsMap[itemId] !== action) {
        setCustomActionsMap({
          ...customActionsMap,
          [itemId]: action,
        });
      }
    },
    [customActionsMap],
  );

  const statusBadgeProps = getEntityStatusBadgeProps(status);

  const statusBadge = {
    label: formatMessage({ id: statusBadgeProps?.labelKey }),
    status: statusBadgeProps?.genesisStatus,
  };

  const isArchived = status === EntityStatusValue.Archived;

  const menuItems = workflowMenuItems.map((item) => {
    if (isCustomAction(item)) {
      const { operation } = item;

      return {
        id: item.id,
        label: isMultiStepOperation(operation) ? (
          <CustomEntityMultiStepActionMenuItem
            entityName={entityConfigName}
            item={item}
            operation={operation}
            domainEntity={domainEntity}
            environment={environmentKey}
            data={entityRootData}
            registerCustomMenuAction={registerCustomMenuAction}
            options={operations?.custom?.options}
          />
        ) : (
          <CustomEntityActionMenuItem
            entityName={entityConfigName}
            item={item}
            operation={operation}
            domainEntity={domainEntity}
            instanceId={instanceId}
            environment={environmentKey}
            data={entityRootData}
            registerCustomMenuAction={registerCustomMenuAction}
            options={operations?.custom?.options}
          />
        ),
      };
    }

    return {
      id: item.type,
      label: <EntityActionMenuItem item={item} />,
    };
  });

  const onMenuItemClicked = React.useCallback(
    (itemId: EntityDetailActionType | string) => {
      const menuItem = workflowMenuItems.find((item) =>
        isCustomAction(item) ? item.id === itemId : item.type === itemId,
      );
      if (isCustomAction(menuItem) && itemId in customActionsMap) {
        customActionsMap[itemId]();
      } else {
        menuItem?.action?.();
      }
    },
    [workflowMenuItems, customActionsMap],
  );

  const menuItemGroup = { menuItems, onMenuItemClicked };

  /**
   * Check if the entity adds a custom path to the entity name or whether
   * it uses the {@link EntityMetadata} interface.
   */
  const isNameInEntityMetadata =
    isUsingConfigAppBackend({
      entityLevelOverride: domainEntity.isUsingConfigAppBackend,
    }) || !schema['x-entity-metadata']?.entityNamePath;

  const environmentSyncAvailable =
    otherAuthoringEnvironmentState?.environmentKey && instanceId;

  const sourceEnvironmentName = getEnvironmentDisplayName(
    currentAuthoringEnvironment,
  );
  const targetEnvironmentName = otherAuthoringEnvironmentState?.environmentKey
    ? getEnvironmentDisplayName(otherAuthoringEnvironmentState.environmentKey)
    : '';

  return (
    <>
      {hasEntityOperation(domainEntity, 'deepClone') && (
        <DeepCloneModalContainer
          currentAuthoringEnvironment={currentAuthoringEnvironment}
          isOpen={isDeepCloneModalOpen}
          setIsOpen={setIsDeepCloneModalOpen}
          deepCloneOptions={operations?.deepClone?.options}
          domainEntity={domainEntity}
        />
      )}
      {isDeepPublishModalOpen && (
        <DeepPublishModalContainer
          isOpen={isDeepPublishModalOpen}
          tree={deepPublish.tree}
          failures={deepPublish.failures}
          isDiscovering={deepPublish.isDiscovering}
          discoveryErrorMessage={deepPublish.discoveryErrorMessage}
          rootEntityId={deepPublish.rootEntityId}
          publishDangerously={publishDangerously}
          environmentName={environmentName}
          onConfirm={handleDeepPublishConfirm}
          onClose={handleDeepPublishClose}
        />
      )}
      {bulkActionReportingSession && (
        <BulkActionReportingModalContainer
          open
          session={bulkActionReportingSession}
          onOpenChange={handleReportingModalOpenChange}
          onCancelCTA={handleCancelPublish}
          onRetryCTA={handleRetryPublish}
          onDoneCTA={handleReportingModalDone}
        />
      )}
      {environmentSyncAvailable && (
        <EntityDataTableModal
          isOpen={isSyncModalOpen}
          onOpenChange={() => setIsSyncModalOpen(!isSyncModalOpen)}
          entityName={entityConfigName}
          currentEnvironmentData={{
            key: currentAuthoringEnvironment,
            data: entityRootData,
          }}
          otherEnvironmentData={
            otherAuthoringEnvironmentState?.environmentKey && {
              key: otherAuthoringEnvironmentState.environmentKey,
              data: otherAuthoringEnvironmentState.entityData,
            }
          }
          entityTreeNodeMap={entityTreeNodeMap}
          syncMode={{
            sourceEnvironment: {
              name: sourceEnvironmentName,
              entityStatus: status,
            },
            targetEnvironment: {
              name: targetEnvironmentName,
              entityStatus: otherAuthoringEnvironmentState?.entityStatus,
            },
            onSyncConfirm: otherAuthoringEnvironmentState?.syncToEnvironment!,
          }}
        />
      )}
      <EntityDetailsHeader
        isLoading={isLoading}
        exitModalPrimaryButtonPath={entityListPath}
        menuItemGroup={menuItemGroup}
        secondaryAction={secondaryAction}
        primaryAction={primaryAction}
        statusBadge={statusBadge}
        entityInstanceName={name || defaultEntityName}
        isEntitySaved={isEntitySaved}
        saveStatusMessage={saveStatusMessage}
        isArchived={isArchived}
        entityConfigName={entityConfigName}
        instanceId={entityInstanceId}
        currentAuthoringEnvironment={currentAuthoringEnvironment}
        availableEnvironments={availableEnvironments}
        multiEnvironmentActions={multiEnvironmentActions}
        isNameEditable={isNameInEntityMetadata}
        setEntityName={setEntityName}
        multiEnvironmentAuthoringEnabled={multiEnvironmentAuthoringEnabled}
        resetEntityData={resetEntityData}
        isEntitySingleEnv={isEntitySingleEnv}
      />
    </>
  );
};
